import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cloneDeep, filter, find, get, includes, isEqual, map, pick, reduce, set } from 'lodash';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import AgentPhonePreferences from '@ad-scripts/domain/models/agent_phone_preferences';
import agentSkillsResource from 'src/actions/skills/agentSkillsResource';
import agentUsersResource, { UserRoles } from 'src/actions/agentUsers/agentUsersResource';
import { AnalystRole } from '@ad-scripts/domain/models/role';
import { Body, Section, SectionDescription, SectionHeader } from 'src/presentation/components/forms/Layout';
import { Button, Checkbox } from 'src/presentation/components/agent_desktop_components';
import ConfirmCancelModal from 'src/presentation/components/forms/ConfirmCancelModal';
import ConfirmSaveModal from './ConfirmSaveModal';
import FieldContainer from 'src/presentation/components/forms/FieldContainer';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm } from 'src/presentation/components/forms/Form';
import generateId from 'src/lib/generateId';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import InputField from 'src/presentation/components/forms/InputField';
import { Label } from '@ad-scripts/presentation/components/common/radio_button';
import MultiselectChips from 'src/presentation/components/forms/MultiselectChips';
import RadioGroup from 'src/presentation/components/forms/RadioGroup';
import reportBuilderConfigResource, {
  ReportBuilderConfig,
} from 'src/actions/reportBuilderConfig/reportBuilderConfigResource';
import { Role } from 'src/actions/roles/rolesResource';
import RoleNotes from './RoleNotes';
import RoutingGroup from 'src/models/RoutingGroup/RoutingGroup';
import routingGroupsResource from 'src/actions/routingGroups/routingGroupsResource';
import skillsResource from 'src/actions/skills/skillsResource';
import { Spinner } from 'src/presentation/components/pages/shared/Spinner';
import StackContainer from '@ad-scripts/presentation/components/common/containers/stack_container';
import teamsResource from 'src/actions/teams/teamsResource';
import { useDropdownOptions } from 'src/presentation/components/pages/skills/hierarchy';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';
import useResource, { STATUS_LOADING, STATUS_SUCCEEDED, useResourceValues } from 'src/presentation/hooks/useResource';
import { useResourceValue } from 'src/presentation/hooks/useDocumentResource';
import useRoles from 'src/presentation/components/pages/people/hooks/useRoles';

enum UserState {
  ACTIVE = 'active',
  DEACTIVATED = 'deactivated',
}

const dataAidPrefix = 'admin-userEditor';
const defaultItem = {
  name: '',
  email: '',
  roleIds: [UserRoles.AGENT],
  routingGroupIds: [],
  skillIds: [],
  teamIds: [],
};

const setOptions = items => items.map(({ id, name }) => ({ label: name, value: id }));

const useOnSave = ({ setComplete, isSSOEnabled, isWebRTCEnabled, isStationIdEnabled }) => {
  const dispatch = useDispatch();
  return ({ add, isNew, item, resetErrors, update, validate, workingStateRef }) => {
    dispatch(resetErrors());

    const currentWorkingState = workingStateRef.current;
    const workingStateToUpdate = pick(
      {
        ...currentWorkingState,
        username: currentWorkingState.email,
        id: isNew ? generateId() : currentWorkingState.id,
      },
      [
        'id',
        'disabledAt',
        'email',
        'name',
        'roleIds',
        'routingGroupIds',
        'skillIds',
        'teamIds',
        'username',
        'voiceConfiguration',
      ]
    );
    if (isStationIdEnabled) {
      const stationId = currentWorkingState.voiceConfiguration?.stationId;
      const phone = currentWorkingState.voiceConfiguration?.phone;
      const defaultPhonePreference = isWebRTCEnabled ? AgentPhonePreferences.BROWSER : AgentPhonePreferences.NONE;
      const voiceConfiguration = {
        phone,
        stationId,
        preference: stationId ? AgentPhonePreferences.STATION : defaultPhonePreference,
      };
      workingStateToUpdate.voiceConfiguration = voiceConfiguration;
    }

    if (!dispatch(validate(workingStateToUpdate))) {
      return;
    }
    setComplete(true);
    const customToastMsg = isSSOEnabled
      ? `Added user "${workingStateToUpdate.name || workingStateToUpdate.email}"`
      : `Invite sent to ${workingStateToUpdate.email}`;
    isNew
      ? dispatch(add({}, workingStateToUpdate, { customToastMsg }))
      : dispatch(update({ id: item.id }, workingStateToUpdate));
  };
};

const RolesCheckboxRows = ({ roles, selectedRoleIds, onRoleFieldChange, errors, isReportBuilderEnabled }) => {
  const allRoles = useMemo(() => {
    return (roles as Role[])
      .filter(role => {
        if (role.disabledAt) return false;

        if (role.id === AnalystRole.id && !isReportBuilderEnabled && !includes(selectedRoleIds, AnalystRole.id)) {
          return false;
        }

        return true;
      })
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [roles, isReportBuilderEnabled, selectedRoleIds]);
  return (
    <>
      {map(allRoles, role => (
        <StackContainer key={role.id} inset="none" stack="small">
          <StyledCheckbox
            checked={includes(selectedRoleIds, role.id)}
            className="role-checkbox"
            data-aid={`role-${role.id}`}
            label={role.name}
            value={role.id}
            onChange={onRoleFieldChange}
          />
          {includes(selectedRoleIds, role.id) && RoleNotes[role.id] && (
            <StyledCheckboxNote data-aid={`role-${role.id}-note`}>{RoleNotes[role.id]}</StyledCheckboxNote>
          )}
        </StackContainer>
      ))}
      {map(
        filter(errors, ({ attr }) => attr === 'roleIds'),
        error => (
          <StyledErrorSection>{error.detail}</StyledErrorSection>
        )
      )}
    </>
  );
};

const UserEditor = ({ root, errors, item }) => {
  const { add, update, validate } = agentUsersResource.effects;
  const { resetErrors } = agentUsersResource.actions;
  const { values: rawRoutingGroups } = useResourceValues(routingGroupsResource);
  const { values: skills } = useResourceValues(skillsResource);
  const { values: teams } = useResourceValues(teamsResource);
  const [isComplete, setComplete] = useState(false);
  const isFeatureEnabled = useIsFeatureEnabled();
  const isSSOEnabled = isFeatureEnabled('sso');
  const isWebRTCEnabled = isFeatureEnabled('webRTC');

  const { roles } = useRoles();
  const { value: reportBuilderConfiguration } = useResourceValue<ReportBuilderConfig>(reportBuilderConfigResource);
  const isReportBuilderEnabled = reportBuilderConfiguration?.enabled;
  const isStationIdEnabled = isFeatureEnabled('telephonyApi') || isFeatureEnabled('sip');
  const handleOnSave = useOnSave({ setComplete, isSSOEnabled, isWebRTCEnabled, isStationIdEnabled });

  const routingGroups = useMemo<{ enabled: RoutingGroup[]; disabled: RoutingGroup[] }>(
    () =>
      reduce(
        rawRoutingGroups,
        (acc: any, routingGroup: RoutingGroup) => {
          const item = { label: routingGroup.name, value: routingGroup.id };

          routingGroup.disabled
            ? acc.disabled.push({ ...item, label: `${item.label} (deactivated)` })
            : acc.enabled.push(item);
          return acc;
        },
        { enabled: [], disabled: [] }
      ),
    [rawRoutingGroups]
  );

  const formContext = useForm({
    add,
    defaultItem,
    onSave: handleOnSave,
    errors,
    item,
    resetErrors,
    root,
    update,
    validate,
  });
  const { isNew, onSave, onClose, updateState, workingStateRef, workingState } = formContext;

  const [areAgentSkillsLoaded, setAgentSkillsLoaded] = useState(false);
  const [isConfirmCancelModalVisible, setConfirmCancelModalVisible] = useState(false);
  const [isConfirmSaveModalVisible, setConfirmSaveModalVisible] = useState(false);
  const initialValue = useRef();
  const isEnableUserManagement = isFeatureEnabled('userManagement');
  useEffect(() => {
    if (!isEnableUserManagement) {
      onClose();
    }
    // eslint-disable-next-line
  }, [isEnableUserManagement]);

  useEffect(() => {
    if ((isNew || areAgentSkillsLoaded) && !initialValue.current) {
      initialValue.current = cloneDeep(workingState);
    }
  }, [areAgentSkillsLoaded, workingState, isNew]);

  // cancel modal
  const isModified = useMemo(
    () => initialValue.current && !isEqual(initialValue.current, workingState),
    [workingState, initialValue]
  );

  const handleStay = useCallback(() => {
    setConfirmCancelModalVisible(false);
  }, [setConfirmCancelModalVisible]);

  const confirmCancel = useCallback(() => {
    isModified ? setConfirmCancelModalVisible(true) : onClose();
  }, [isModified, onClose, setConfirmCancelModalVisible]);

  // save modal
  const triggerSave = useCallback(() => {
    if (includes(workingState.roleIds, AnalystRole.id)) {
      setConfirmSaveModalVisible(true);
    } else {
      onSave();
    }
  }, [onSave, workingState]);

  const onRoleFieldChange = useCallback(
    e => {
      const roleId = e.target.value;
      const selectedRoleIds = workingStateRef.current.roleIds;
      let roleIds;
      if (includes(selectedRoleIds, roleId)) {
        roleIds = filter(selectedRoleIds, id => id !== roleId);
      } else {
        if (roleId === UserRoles.LIVEBOARDS || roleId === UserRoles.TASK_USER) {
          roleIds = [roleId];
        } else {
          let nextRoleIds = workingStateRef.current.roleIds;
          if (find(selectedRoleIds, role => role === UserRoles.LIVEBOARDS)) {
            nextRoleIds = filter(selectedRoleIds, role => role !== UserRoles.LIVEBOARDS);
          } else if (find(selectedRoleIds, role => role === UserRoles.TASK_USER)) {
            nextRoleIds = filter(selectedRoleIds, role => role !== UserRoles.TASK_USER);
          }
          roleIds = nextRoleIds.concat(roleId);
        }
      }

      updateState({
        ...workingStateRef,
        roleIds,
      });
    },
    [workingStateRef, updateState]
  );

  const stateValue = get(workingState, 'disabledAt') ? UserState.DEACTIVATED : UserState.ACTIVE;

  const onStateChangeHandle = event => {
    const value = event.target.value === UserState.DEACTIVATED ? new Date().toISOString() : null;
    updateState(set({}, 'disabledAt', value));
  };

  const skillsOptions: any[] = [];
  const removableSkillsOptions: any[] = [];
  useDropdownOptions(skills).forEach(skill => {
    if (skill.disabled) {
      removableSkillsOptions.push({ label: skill.label + ' (archived)', value: skill.value });
    } else {
      skillsOptions.push({ label: skill.label, value: skill.value });
    }
  });

  const { loadingState: agentSkillsLoadingState, values: agentSkills } = useResource(agentSkillsResource, {
    fetch: !isNew, // only fetch agent skills for existing user
    params: { agentIds: !isNew ? item.id : '' },
  });

  useEffect(() => {
    if (agentSkillsLoadingState === STATUS_SUCCEEDED) {
      const skillIds = get(agentSkills[0], 'skillIds');
      updateState(set({}, 'skillIds', skillIds));
      setAgentSkillsLoaded(true);
    }
  }, [agentSkills, agentSkillsLoadingState, updateState]);

  if (agentSkillsLoadingState === STATUS_LOADING) {
    return <Spinner />;
  }

  return (
    <>
      <Form context={formContext} preventSaveOnEnter>
        <H1>{isNew ? 'Add' : 'Edit'} User</H1>
        <Body>
          <Row>
            <InputField autoFocus data-aid="email" attr="email" label="Email" placeholder="name@yourcompany.com" />
          </Row>
          <Row>
            <InputField data-aid="name" attr="name" label="Name" placeholder="Enter a name" />
          </Row>

          <Row>
            {!isNew && (
              <StyledRadioGroup
                label="Status"
                attr=""
                className="radioGroup"
                data-aid="enabled-radio-group"
                onChange={onStateChangeHandle}
                options={[
                  {
                    label: 'Active',
                    value: UserState.ACTIVE,
                  },
                  {
                    label: 'Deactivated',
                    value: UserState.DEACTIVATED,
                  },
                ]}
                value={stateValue}
              />
            )}
          </Row>

          {isStationIdEnabled ? (
            <Row>
              <InputField
                data-aid="stationId"
                attr="voiceConfiguration.stationId"
                label="Station ID"
                placeholder="Station ID"
              />
            </Row>
          ) : null}

          <StyledSection
            className="styled-section"
            header="Roles"
            description={
              <>
                To learn about role permissions, see{' '}
                <ExternalLink
                  data-aid="learn-about-roles"
                  label="Roles and Responsibilities"
                  to="https://help.gladly.com/docs/managing-users-and-roles#roles-and-responsibilities"
                />
              </>
            }
          >
            <RolesCheckboxRows
              roles={roles}
              selectedRoleIds={workingState.roleIds}
              onRoleFieldChange={onRoleFieldChange}
              errors={errors}
              isReportBuilderEnabled={isReportBuilderEnabled}
            />
          </StyledSection>

          <Row>
            <MultiselectChips
              attr="routingGroupIds"
              data-aid="inbox-selector"
              label="Inboxes"
              placeholder="Search inboxes"
              options={routingGroups.enabled}
              removableOptions={routingGroups.disabled}
            />
          </Row>

          <Row>
            <MultiselectChips
              attr="teamIds"
              data-aid="team-selector"
              label="Teams"
              placeholder="Search teams"
              options={setOptions(teams)}
            />
          </Row>
          {areAgentSkillsLoaded || isNew ? (
            <Row>
              <MultiselectChips
                attr="skillIds"
                data-aid="skill-selector"
                label="Skills"
                placeholder="Search skills"
                options={skillsOptions}
                removableOptions={removableSkillsOptions}
              />
            </Row>
          ) : null}
        </Body>
        <Footer>
          <Button data-aid={`${dataAidPrefix}-cancelButton`} type={Button.Types.TEXT} onClick={confirmCancel}>
            Cancel
          </Button>
          <Button data-aid={`${dataAidPrefix}-saveButton`} onClick={triggerSave}>
            {isNew ? 'Add User' : 'Save'}
          </Button>
        </Footer>
      </Form>
      <ConfirmCancelModal
        name="User"
        handleLeave={onClose}
        handleStay={handleStay}
        isModified={isModified && !isComplete}
        isVisible={isConfirmCancelModalVisible}
        customText="If you leave this page, your changes will be discarded."
      />
      <ConfirmSaveModal
        onCancel={() => setConfirmSaveModalVisible(false)}
        onSubmit={onSave}
        isVisible={isConfirmSaveModalVisible}
      />
    </>
  );
};

const StyledRadioGroup = styled(RadioGroup)`
  ${Label}:last-child {
    margin-bottom: 0;
  }
`;

const StyledSection = styled(Section)`
  ${SectionHeader} {
    color: ${p => p.theme.colors.black};
    font-size: 13px;
    margin-bottom: 4px;
  }

  ${SectionDescription} {
    column-gap: 4px;
    flex-direction: revert;
  }
`;

const StyledErrorSection = styled.div`
  color: ${p => p.theme.colors.red400};
`;

const StyledCheckbox = styled(Checkbox)`
  white-space: nowrap;
`;

const StyledCheckboxNote = styled.p`
  color: ${p => p.theme.colors.gray700};
  margin: 8px 0 0 24px;
`;

export const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin-bottom: 24px;

  > ${FieldContainer} {
    margin: 0 12px;
  }
  > ${FieldContainer}:first-child {
    margin-left: 0;
  }
  > ${FieldContainer}:last-child {
    margin-right: 0;
  }
`;

export default React.memo(UserEditor);
